import { useState } from 'react'
import { api } from './api'
import { Auswahlfeld } from './Auswahlfeld'
import { Dialogfenster } from './Dialogfenster'
import { meldeErfolg } from './Erfolgsstelle'
import { fachlicherSatz } from './Fehlersatz'
import { LEERE_REFERENZ, Referenzentwurfstabelle, artfelder, quellfelder, referenzfehler, useReferenzlisten } from './Kfreigenschaften'
import type { Referenzentwurf } from './Kfreigenschaften'
import { Hinweisfenster, Nachfrage } from './Nachfrage'
import { useDarf } from './Rechte'
import {
  aktuelleSprache,
  beschriftung,
  bewegungsartname,
  kontobezeichnung,
  merkeStatistikkonto,
  positionsbezeichnung,
  t,
} from './sprache'
import type { Kfrreferenzkopf } from './typen'

/**
 * **Die Referenzen einer Position als eigene Zeilen** (2.1 Abschnitt 3.6,
 * R1710, R1712).
 *
 * Die Entscheidung des Auftraggebers vom 2026-10-14: *„Nachdem ich eine
 * Referenz eingefügt habe, erwarte ich eine eigene Zeile, die diese Referenz
 * anzeigt … Wenn ich auf eine Referenz rechtsklicke, wird für exakt diese
 * Referenz die Löschoption angeboten."* Bis dahin standen die Referenzen nur
 * im Eigenschaftsfenster der Position, ohne Betrag.
 */

/** Wie eine Referenz nach ihrer Quelle heißt — *„B.II.1 Forderungen · Zugang"*. */
export function quellname(r: Pick<Kfrreferenzkopf, 'position' | 'konto' | 'bewegungsarten' | 'umkehren'>): string {
  return [
    r.position ? positionsbezeichnung(r.position) : r.konto ? kontobezeichnung(r.konto) : '',
    r.bewegungsarten.map(bewegungsartname).join(', '),
    r.umkehren ? t('kfr.referenz.umgekehrt') : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * **Was in der Zeile steht: die eigene Bezeichnung, sonst die Quelle** (R1710).
 * Keine Prüfung liest sie — sie ist für den Leser.
 */
export function referenzname(r: Kfrreferenzkopf): string {
  // Ein Statistikkonto heißt wie das Konto (2.1 Abschnitt 3.6, R1727).
  if (r.art === 'statistik' && r.konto) {
    return kontobezeichnung(r.konto)
  }
  return beschriftung(r.namen) || quellname(r)
}

/**
 * **Rechtsklick → Neues Konto …** an einer Position der Kapitalflussrechnung
 * (2.1 Abschnitt 3.6, R1715, R1727): Es entsteht ein Statistikkonto, unter
 * genau dieser Position. Gefragt wird nur die Bezeichnung, mit der Nummer
 * vorn (R678); das Fenster sagt, was ein Statistikkonto ist.
 */
export function Statistikkontoanlage({
  zeile,
  titel,
  aufAngelegt,
  aufSchliessen,
}: {
  /** Die Position, unter der das Konto entsteht. */
  zeile: string
  /** Nummer und Name der Position, wie sie in der Rechnung steht. */
  titel: string
  aufAngelegt: () => void
  aufSchliessen: () => void
}) {
  // Was der Angemeldete nicht darf, ist nicht da (R1180).
  const darf = useDarf()
  const darfAnlegen = darf('statistikkontoAnlegen')
  const [bezeichnung, setBezeichnung] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const bereit = darfAnlegen && bezeichnung.trim().length > 0 && !laeuft

  async function anlegen() {
    if (!bereit) {
      return
    }
    setLaeuft(true)
    setMeldung(null)
    try {
      const neu = await api.statistikkontoAnlegen(zeile, bezeichnung.trim())
      // Gleich aus jeder anderen Auswahl heraus, bis der Katalog es selbst nennt (R1734).
      merkeStatistikkonto(neu.konto)
      meldeErfolg(t('erfolg.statistikkonto.angelegt'))
      aufAngelegt()
      aufSchliessen()
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel={t('kfr.statistik.anlegen.titel', { position: titel })}
      weite="mittel"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          {darfAnlegen && (
            <button type="button" className="haupt" disabled={!bereit} onClick={() => void anlegen()}>
              {t('anlage.anlegen')}
            </button>
          )}
          <button type="button" onClick={aufSchliessen}>
            {t('import.schliessen')}
          </button>
        </>
      }
    >
      <div className="strukturanlage">
        <p className="hinweis">{t('kfr.statistik.anlegen.hinweis')}</p>
        <div className="anlagefelder">
          <label className="feld breit">
            <span className="name">{t('anlage.bezeichnung')}</span>
            <input
              value={bezeichnung}
              disabled={laeuft}
              autoFocus
              onChange={(e) => setBezeichnung(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void anlegen()
                }
              }}
            />
          </label>
        </div>
        {meldung && <p className="hinweis">{meldung}</p>}
      </div>
    </Dialogfenster>
  )
}

/**
 * **Rechtsklick → Neue Referenz …** an einer Position (2.1 Abschnitt 3.6,
 * R1705, R1710).
 *
 * Gefragt wird, was eine Referenz ist: *Referenz auf Position/Konto*,
 * Bewegungsarten und *Vorzeichen umkehren* (R1355, R1356), dazu die
 * Bezeichnung — vorbelegt mit der Quelle, bis der Anwender sie selbst
 * schreibt. Unter dem Finanzmittelfonds nur die Quelle: Der Fonds hat weder
 * Bewegungsart noch Vorzeichen (R1338).
 */
export function Referenzanlage({
  zeile,
  titel,
  fonds,
  aufAngelegt,
  aufSchliessen,
}: {
  /** Die Position, unter der die Referenz entsteht. */
  zeile: string
  /** Nummer und Name der Position, wie sie in der Rechnung steht. */
  titel: string
  /** Eine Referenz des Finanzmittelfonds (R1712). */
  fonds: boolean
  aufAngelegt: () => void
  aufSchliessen: () => void
}) {
  // Was der Angemeldete nicht darf, ist nicht da (8.1 Abschnitt 7, R1180).
  const darf = useDarf()
  const darfAnlegen = darf('stammdatumAnlegen')
  const listen = useReferenzlisten()
  const [entwurf, setEntwurf] = useState<Referenzentwurf>(LEERE_REFERENZ)
  const [name, setName] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  const quelle = quellfelder(entwurf.quelle)
  const vorschlag = quellname({
    position: quelle.position,
    konto: quelle.konto,
    bewegungsarten: fonds ? [] : entwurf.arten,
    umkehren: !fonds && entwurf.umkehren,
  })
  // Die Bezeichnung folgt der Quelle, bis der Anwender selbst schreibt.
  const bezeichnung = name ?? vorschlag
  const vollstaendig = fonds ? entwurf.quelle !== '' : referenzfehler(entwurf.quelle, entwurf.arten) === null
  const bereit = darfAnlegen && vollstaendig && bezeichnung.trim().length > 0 && !laeuft

  async function anlegen() {
    if (!bereit) {
      return
    }
    setLaeuft(true)
    setMeldung(null)
    const felder: Record<string, string | null> = fonds
      ? { ...quelle }
      : {
          zeile,
          ...quelle,
          ...artfelder(entwurf.arten),
          vorzeichen_umkehren: entwurf.umkehren ? 'true' : 'false',
        }
    try {
      await api.stammdatumAnlegen(fonds ? 'kfr_fonds' : 'kfr_zuordnung', '', {
        felder: Object.fromEntries(Object.entries(felder).filter(([, w]) => w !== null)) as Record<string, string>,
        texte: [{ sprache: aktuelleSprache(), textart: 'name', text: bezeichnung.trim() }],
      })
      meldeErfolg(t('erfolg.referenz.angelegt'))
      aufAngelegt()
      aufSchliessen()
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel={t('kfr.referenz.anlegen.titel', { position: titel })}
      weite="mittel"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          {darfAnlegen && (
            <button type="button" className="haupt" disabled={!bereit} onClick={() => void anlegen()}>
              {t('anlage.anlegen')}
            </button>
          )}
          <button type="button" onClick={aufSchliessen}>
            {t('import.schliessen')}
          </button>
        </>
      }
    >
      <div className="strukturanlage">
        {fonds ? (
          listen && (
            <label className="feld breit">
              <span className="name">{t('kfreigenschaften.quelle')}</span>
              <Auswahlfeld
                wert={entwurf.quelle}
                eintraege={listen.quellliste}
                leer={t('auswahl.bitte')}
                abgeschaltet={laeuft}
                beschriftung={t('kfreigenschaften.quelle')}
                aufWahl={(w) => setEntwurf({ ...entwurf, quelle: w })}
              />
            </label>
          )
        ) : (
          <Referenzentwurfstabelle
            zeilen={[entwurf]}
            einzeln
            abgeschaltet={laeuft}
            aufAenderung={(zeilen) => setEntwurf(zeilen[0] ?? LEERE_REFERENZ)}
          />
        )}

        <div className="anlagefelder">
          <label className="feld breit">
            <span className="name">{t('anlage.bezeichnung')}</span>
            <input value={bezeichnung} disabled={laeuft} onChange={(e) => setName(e.target.value)} />
            <span className="hinweis">{t('kfr.referenz.bezeichnung.hinweis')}</span>
          </label>
        </div>

        {meldung && <p className="hinweis">{meldung}</p>}
      </div>
    </Dialogfenster>
  )
}

/**
 * **Rechtsklick → Referenz löschen …** an genau dieser Referenz (2.1
 * Abschnitt 3.6, R1705, R1707): Vorher fragt das Fenster nach und sagt, was
 * geschieht.
 */
export function Referenzloeschung({
  referenz,
  position,
  aufGeloescht,
  aufSchliessen,
}: {
  referenz: Kfrreferenzkopf
  /** Nummer und Name der Position, unter der sie steht. */
  position: string
  aufGeloescht: () => void
  aufSchliessen: () => void
}) {
  const darf = useDarf()
  const [meldung, setMeldung] = useState<string | null>(null)
  const titel = t('kfr.referenz.loeschen.titel')

  if (meldung) {
    return <Hinweisfenster titel={titel} meldung={meldung} aufSchliessen={aufSchliessen} />
  }

  // Was der Angemeldete nicht darf, ist nicht da (R1180) — das Menü bietet
  // den Punkt dann gar nicht erst an; diese Zeile hält auch den Weg dahinter zu.
  if (!darf('aussondern')) {
    return null
  }

  return (
    <Nachfrage
      titel={titel}
      frage={t('kfr.referenz.loeschen.frage', { referenz: referenzname(referenz), position })}
      hinweis={t(referenz.art === 'fonds' ? 'kfr.referenz.loeschen.fonds' : 'kfr.referenz.loeschen.hinweis')}
      bestaetigen={t('kfr.loeschen.knopf')}
      gefahr
      aufJa={() => {
        api
          .aussondern(referenz.art === 'fonds' ? 'kfr_fonds' : 'kfr_zuordnung', referenz.schluessel, {
            weg: 'entfernen',
            abPeriode: null,
            ziel: null,
          })
          .then(() => {
            meldeErfolg(t('erfolg.referenz.geloescht'))
            aufGeloescht()
            aufSchliessen()
          })
          .catch((f: unknown) => setMeldung(fachlicherSatz(f) ?? t('kfr.referenz.loeschen.fehler')))
      }}
      aufSchliessen={aufSchliessen}
    />
  )
}
